import dayjs from 'dayjs';
import { AxiosError, AxiosResponse } from 'axios';
import '@/styles/pages/CareerHistory/Edit.scss';

import React, { useState, useEffect } from 'react';
import { FieldValues, useForm, UseFormProps, UseFormReturn, useFieldArray } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import * as CH from '@/components/CareerHistory/Form/Confirmation';
import {
  FormContainer,
  FormContainerTextArea,
  FormContainerTextfield,
  FormLabel,
} from '@/components/common/Form/';
import { ToggleLabelButton } from '@/components/common/Form/ToggleLabelButton';
import * as Page from '@/components/common/Page';
import { TitleSectionTitle } from '@/components/common/Title/SectionTitle';
import HeaderTitle from '@/components/common/Title/HeaderTitle';
import { NotificationMatchbou } from '@/components/common/Notification/Matchbou';
import Modal from '@/components/common/Modal';
import { ConfirmationModal } from '@/components/common/Preview/ConfirmationModal';
import { CareerHistoryConfirmationPreview } from '@/components/CareerHistory/Preview/InputAgencyPreview';
import BaseButton from '@/components/common/Button/BaseButton';
import { useMBXMediaQuery } from '@/hooks/useMBXMediaQuery';
import { careerHistory, qualification, career } from '@/hooks/usePageCareerHistory';
import {
  CareerHistory,
  ResponseError,
  Project,
  UserInfo,
  Status,
  CareerHistoryApi,
  UserApi,
  CareerHistorySkills,
  MBResumeResponse,
  ResumeApi,
  Qualification,
  Career,
} from '@/utils/api-client';
import { setSaved, setPrepareFrag, toggleLoading, notificationError } from '@/redux/index';
import { State } from '@/redux/state';

export type CareerHistoryCheckProps = {
  job_check?: boolean;
  description_check?: boolean;
  advantage_check?: boolean;
  careers_check?: boolean;
  skills_check?: boolean;
  qualifications_check?: boolean;
  pr_check?: boolean;
};

type TCheckInput = {
  resume_update: boolean;
  resume_completed: boolean;
  cv_update: boolean;
  cv_completed: boolean;
};

export const CareerHistoryConfirmationEdit = (): React.ReactElement => {
  const mq = useMBXMediaQuery();
  const userState = useSelector((state: State) => state.user);
  const dispatch = useDispatch();
  const [CHData, setCHData] = useState<
    (CareerHistory & Status & UserInfo & CareerHistoryCheckProps) | undefined
  >();
  const [updatedKey, setUpdatedKey] = useState<string[]>([]);
  const [isFirstGet, setIsFirstGet] = useState<boolean>(false);
  const [preview, setPreview] = useState<boolean>(true);
  const [toggleCareers, setToggleCareers] = useState<string[]>([]);
  const [deleteCareers, setDeleteCareers] = useState<string[]>([]);
  const [deleteProjects, setDeleteProjects] = useState<string[]>([]);
  const [toggleQualifications, setToggleQualifications] = useState<string[]>([]);
  const [deleteQualifications, setDeleteQualifications] = useState<string[]>([]);
  const [deleteskills, setDeleteskills] = useState<number[]>([]);
  const [notification, setNotification] = useState<boolean>(true);
  const [modal, setModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<number>(1);
  const [allCheck, setAllCheck] = useState<boolean>(true);
  const [scrollCheck, setScrollCheck] = useState<boolean>(false);
  const [scrolling, setScrolling] = useState<boolean>(false);
  const [checkInputData, setCheckInputData] = useState<TCheckInput>();
  const {
    data: fetchData,
    user,
    setDefaultValue,
    patch: patchCareerHistoryImmediately,
    deleteSkill,
    postQualification,
    postCareer,
    removeCareer,
    deleteQualification,
  } = careerHistory(true, 'input_agency');
  const [previewChData, setPreviewChData] = useState<
    (CareerHistory & Status & UserInfo) | undefined
  >();
  const { data: originalData } = careerHistory(true);
  const { patch: patchToggleQualification } = qualification();
  const { patch, removeProject } = career();
  const history = useHistory();
  const checkList = {
    job_check: true,
    description_check: true,
    advantage_check: true,
    careers_check: true,
    skills_check: true,
    qualifications_check: true,
    pr_check: true,
  };
  const cvKey = [
    'advantage',
    'description',
    'job',
    'latest_update',
    'pr',
    'careers',
    'skills',
    'qualifications',
  ];
  type TCvKey =
    | 'advantage'
    | 'description'
    | 'job'
    | 'latest_update'
    | 'pr'
    | 'careers'
    | 'skills'
    | 'qualifications';

  useEffect(() => {
    // 遷移して初回のみ取得する
    if (fetchData && !isFirstGet) {
      checkInputAgencyStatus();
      // firstSetData();
    }
  }, [fetchData]);

  const firstSetData = () => {
    if (fetchData) {
      const data = {
        ...fetchData,
        ...checkList,
        latest_update: dayjs().format('YYYY-MM-DD'),
      };
      setCHData(data);
      setPreviewChData({ ...fetchData, latest_update: dayjs().format('YYYY-MM-DD') });
      setIsFirstGet(true);
    }
  };

  const checkInputAgencyStatus = async () => {
    const res = await new UserApi().getInputAgency();
    if (res.data && res.data.status !== 3) {
      history.push('/mypage/self-resume-cv/edit');
    } else {
      checkInput();
    }
  };

  const checkInput = async () => {
    const get_data = localStorage.getItem('checkInputData');
    if (get_data) {
      const parse_data: TCheckInput = JSON.parse(get_data);
      setCheckInputData(parse_data);
      if (!parse_data.cv_update || parse_data.cv_completed) {
        history.push('/mypage/resume/confirmation');
      } else {
        firstSetData();
      }
    } else {
      setModal(true);
      const data = {
        resume_update: false,
        resume_completed: false,
        cv_update: false,
        cv_completed: false,
      };
      const InputAgencyResumeRes: AxiosResponse<MBResumeResponse> =
        await new ResumeApi().getInputAgencyResume();
      const InputAgencyCHRes: AxiosResponse<CareerHistory & Status & UserInfo> =
        await new CareerHistoryApi().getInputAgencyCareerHistory();
      if (InputAgencyResumeRes.data.user?.last_update_resume) {
        data.resume_update = true;
      } else {
        data.resume_completed = true;
      }
      if (InputAgencyCHRes.data.latest_update) {
        data.cv_update = true;
      } else {
        data.cv_completed = true;
      }
      if (!InputAgencyCHRes.data.latest_update) {
        history.push('/mypage/resume/confirmation');
      } else {
        localStorage.setItem('checkInputData', JSON.stringify(data));
        setCheckInputData(data);
        firstSetData();
      }
    }
  };

  const useFormProps: UseFormProps = {
    mode: 'onBlur',
    // FormContainerはfetchDataを監視しているが、fetchDataそのものをsetValueしないので、
    // defaultValuesを更新する必要がある
    defaultValues: setDefaultValue(CHData),
  };

  const methods = useForm(useFormProps);
  const control = methods.control;
  const values = methods.watch();
  const { update } = useFieldArray({
    name: 'ch',
    control,
  });

  const changeAllCheck = () => {
    methods.setValue('job_check', !allCheck);
    methods.setValue('description_check', !allCheck);
    methods.setValue('advantage_check', !allCheck);
    methods.setValue('careers_check', !allCheck);
    methods.setValue('skills_check', !allCheck);
    methods.setValue('qualifications_check', !allCheck);
    methods.setValue('pr_check', !allCheck);
    setAllCheck(!allCheck);
  };

  const placeholder = {
    advantage: `例）
①Webサイト構築およびアプリ開発におけるUI/UX設計（主にFigmaを使用）
②ラフ案およびワイヤーフレームの作成（主にAdobe XD、Photoshop、Illustratorを使用）
③フロントエンド領域でのコーディング（主にHTML、CSS、JavaScript、PHPを使用）`,
  };

  const setBeforeUnload = (flag: boolean) => {
    dispatch(setPrepareFrag(flag));
  };

  // const isInclude = (name: string) => {
  //   return updatedKey.some((key) => key.includes(name));
  // };

  const onSubmit = async () => {
    console.log('onSubmit');
  };

  const clickSubmit = async () => {
    const formData = values;
    const data: any = {
      latest_update: formData.latest_update,
    };
    dispatch(toggleLoading(true));
    try {
      // 更新があったkeyのみを変換して利用する
      // Object.keys(formData).forEach(function (key) {
      //   if (!updatedKey.some((uKey) => uKey.includes(key))) return;
      //   data[key] = formData[key];
      // });

      if (originalData) {
        // 職歴削除処理
        if (formData.careers_check) {
          if (originalData?.careers && originalData?.careers?.length !== 0) {
            originalData?.careers.forEach(async (el) => {
              if (el.c_projects && el.c_projects.length !== 0) {
                el.c_projects.forEach(async (pj) => {
                  if (pj.id) await removeProject(pj.id);
                });
              }
              await removeCareer(el.c_id);
            });
            // for (let i = 0; i < originalData?.careers?.length; i++) {
            //   await removeCareer(deleteCareers[i]);
            // }
            // setDeleteCareers([]);
          }
        }

        // // 職歴・プロジェクト削除処理
        // if (deleteProjects.length !== 0) {
        //   for (let i = 0; i < deleteProjects.length; i++) {
        //     await removeProject(Number(deleteProjects[i]));
        //   }
        //   setDeleteProjects([]);
        // }

        // 資格・免許削除処理
        if (formData.qualifications_check) {
          if (originalData?.qualifications && originalData?.qualifications?.length !== 0) {
            originalData?.qualifications.forEach(async (el) => {
              await deleteQualification(el.q_id);
            });
            // for (let i = 0; i < deleteQualifications.length; i++) {
            //   await deleteQualification(deleteQualifications[i]);
            // }
            // setDeleteQualifications([]);
          }
        }

        // スキル削除処理
        if (formData.skills_check) {
          if (originalData?.skills && originalData?.skills.length !== 0) {
            originalData?.skills.forEach(async (el) => {
              if (el.id) await deleteSkill(el.id);
            });
            // for (let i = 0; i < deleteskills.length; i++) {
            //   await deleteSkill(deleteskills[i]);
            // }
            // setDeleteskills([]);
          }
          if (formData.skills) {
            formData.skills.forEach(async (el: CareerHistorySkills) => {
              if (el.id) delete el.id;
            });
          }
        }
      }

      Object.keys(formData).forEach(function (key) {
        if (cvKey.includes(key)) {
          if (formData[key + '_check']) {
            data[key] = formData[key];
          }
        }
      });

      // 資格・免許
      if (formData.qualifications_check) {
        for (let i = 0; i < formData.qualifications?.length; i++) {
          const qualification = formData.qualifications[i];
          if (qualification.q_id) delete qualification.q_id;
          const res = await postQualification(qualification);
          formData.qualifications[i] = res?.data.qualifications[0];
          // if (!qualification.q_id) {
          //   // 登録処理
          //   const res = await postQualification(qualification);
          //   formData.qualifications[i] = res?.data.qualifications[0];
          // } else if (
          //   isInclude(`qualifications.${i}`) ||
          //   toggleQualifications.includes(qualification.q_id)
          // ) {
          //   // 更新処理
          //   await patchToggleQualification(qualification, false);
          // }
        }
        data.qualifications = formData.qualifications;
      }

      // 職歴
      if (formData.careers_check) {
        for (let i = 0; i < formData.careers?.length; i++) {
          const career = formData.careers[i];
          if (career.c_employee_count === '') career.c_employee_count = 0;
          if (career.c_capital === '') career.c_capital = 0;
          career.c_projects.forEach((project: Project) => {
            if (!project.id) delete project.id;
          });
          if (career.c_id) delete career.c_id; // 代行のidは消す
          const res = await postCareer(career);
          formData.careers[i] = res?.data.careers[0];
          // if (!career.c_id) {
          //   // 登録処理
          //   const res = await postCareer(career);
          //   formData.careers[i] = res?.data.careers[0];
          // } else if (isInclude(`careers.${i}`) || toggleCareers.includes(career.c_id)) {
          //   // 更新処理
          //   const res = await patch(career);
          //   formData.careers[i] = res?.data.careers[0];
          // }
        }
        data.careers = formData.careers;
      }

      // 職務経歴書更新
      await patchCareerHistoryImmediately(data, false);
      // await new UserApi().patchInputAgency({ status: 4 });

      // if (user?.user_id) {
      //   const res = await new CareerHistoryApi().getInputAgencyCareerHistory();
      //   setCHData({ ...res.data, ...checkList });
      // }

      // await new UserApi().patchInputAgency({ status: 4 });

      // setToggleQualifications([]);
      // setToggleCareers([]);
      // setUpdatedKey([]);
      setBeforeUnload(false);
      dispatch(setSaved(true));

      const saveData = checkInputData;
      if (saveData) saveData.cv_completed = true;
      if (checkInputData?.resume_completed) {
        await new UserApi().patchInputAgency({ status: 4 });
        localStorage.removeItem('checkInputData');
        if (userState?.use_direct) {
          setModalContent(5);
        } else {
          setModalContent(4);
        }
      } else {
        localStorage.setItem('checkInputData', JSON.stringify(saveData));
        setModalContent(3);
      }
      setModal(true);

      // 確認ボタンの場合はプレビューに進む
      // const event = e as React.BaseSyntheticEvent<Record<string, unknown>, any, any> | undefined;
      // const button = event?.nativeEvent?.submitter as HTMLButtonElement;
      // if (button.textContent === '反映を確認する') history.push('/mypage/cv');
    } catch (e) {
      // ロールバック処理
      if (user?.user_id) {
        const res = await new CareerHistoryApi().getCareerHistory(user?.user_id);
        const response = res.data;
        // 資格・免許
        if (response.qualifications && response.qualifications.length !== 0) {
          // 既に登録されていれば削除
          const reQualifications = response.qualifications;
          for (let i = 0; i < reQualifications.length; i++) {
            if (
              CHData?.qualifications &&
              CHData.qualifications.filter(
                (qualification) => qualification.q_id === reQualifications[i].q_id
              ).length === 0
            ) {
              await deleteQualification(reQualifications[i].q_id);
            }
          }
        }
        if (CHData?.qualifications && CHData.qualifications.length !== 0) {
          for (let i = 0; i < CHData.qualifications.length; i++) {
            // 更新処理
            await patchToggleQualification(CHData.qualifications[i], false, true);
          }
        }

        // 職歴
        if (response.careers && response.careers.length !== 0) {
          // 既に登録されていれば削除
          const reCareers = response.careers;
          for (let i = 0; i < reCareers.length; i++) {
            if (
              CHData?.careers &&
              CHData.careers.filter((career) => career.c_id === reCareers[i].c_id).length === 0
            ) {
              await removeCareer(reCareers[i].c_id);
            }
          }
        }
        if (CHData?.careers && CHData.careers.length !== 0) {
          for (let i = 0; i < CHData.careers.length; i++) {
            // 更新処理
            await patch(CHData.careers[i]);
          }
        }

        // スキル
        if (response.skills && response.skills.length !== 0) {
          // 既に登録されていれば削除
          const reSkills = response.skills;
          for (let i = 0; i < reSkills.length; i++) {
            if (
              reSkills[i].id &&
              CHData?.skills &&
              CHData.skills.filter((skill) => skill.id === reSkills[i].id).length === 0
            ) {
              await deleteSkill(reSkills[i].id as number);
            }
          }
        }

        await patchCareerHistoryImmediately(CHData, false);
      }
      const error = e as AxiosError<ResponseError>;
      if (error.response) dispatch(notificationError(error.response.data.error_message));
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  const changePreview = async () => {
    dispatch(toggleLoading(true));
    window.scrollTo(0, 0);

    const formData = values;
    const data: any = {
      latest_update: formData.latest_update,
    };

    Object.keys(formData).forEach(function (key) {
      if (cvKey.includes(key)) {
        if (formData[key + '_check']) {
          if (key === 'careers') {
            const prevCareers: Career[] = [];
            formData.careers.forEach((item: any) => {
              if (item.c_join_date_year && item.c_join_date_month) {
                item.c_join_date = item.c_join_date_year + '-' + item.c_join_date_month;
              }
              if (item.c_leave_flag && item.c_leave_date_year && item.c_leave_date_month) {
                item.c_leave_date = item.c_leave_date_year + '-' + item.c_leave_date_month;
              }
              if (item.c_projects.length) {
                item.c_projects.forEach((project: any, i: number) => {
                  if (project.start_date_year && project.start_date_month) {
                    project.start_date = project.start_date_year + '-' + project.start_date_month;
                  }
                  if (project.end_date_year && project.end_date_month) {
                    project.end_date = project.end_date_year + '-' + project.end_date_month;
                  }
                  item.c_projects[i] = project;
                });
              }
              prevCareers.push(item);
            });
            data[key] = prevCareers;
          } else if (key === 'qualifications') {
            const prevQualifications: Qualification[] = [];
            formData.qualifications.forEach((item: any) => {
              item.q_date = item.q_date_year + '-' + item.q_date_month;
              prevQualifications.push(item);
            });
            data[key] = prevQualifications;
          } else {
            data[key] = formData[key];
          }
        } else {
          if (key !== 'latest_update') {
            if (originalData) data[key] = originalData[key as TCvKey];
          }
        }
      }
      // if (!updatedKey.some((uKey) => uKey.includes(key))) return;
    });

    setPreviewChData({
      ...data,
    });
    setPreview(true);
    dispatch(toggleLoading(false));
  };

  const resetData = () => {
    dispatch(toggleLoading(true));
    (async () => {
      // なぜかスキルだけ入力した値を保持してしまうから強制的に上書きする
      const res = await new CareerHistoryApi().getInputAgencyCareerHistory();
      if (fetchData && res) {
        const data = {
          ...fetchData,
          ...checkList,
          skills: res.data.skills,
          latest_update: dayjs().format('YYYY-MM-DD'),
        };
        const formData: {
          [x: string]: any;
        } = setDefaultValue(data);
        Object.keys(formData).forEach(function (key: string) {
          methods.setValue(key, formData[key]);
          // なぜか資格免許が更新されないからひとつづつ更新する
          if (key === 'qualifications' && formData.qualifications.length) {
            formData[key].forEach((item: Qualification, i: number) => {
              Object.keys(item).forEach(function (qkey: string) {
                methods.setValue(
                  'qualifications.' + i + '.' + qkey,
                  item[qkey as keyof Qualification]
                );
              });
            });
          }
        });
      }
    })();
    setAllCheck(true);
    setModal(false);
    dispatch(toggleLoading(false));
  };

  const clickModalClose = () => {
    setModal(false);
    if (modalContent == 4 || modalContent == 5) {
      history.push('/mypage/cv');
    }
  };

  const scrollChange = () => {
    if (document.documentElement.scrollTop < 300) {
      setScrollCheck(false);
    } else {
      setScrollCheck(true);
    }
  };

  const scrollEnd = () => {
    setScrolling(false);
  };

  let timeoutId: number;
  const timeoutScroll = () => {
    if (document.documentElement.scrollTop > 300) {
      setScrolling(true);
    }
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(scrollEnd, 100);
  };

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name && name.indexOf('_check') > -1) {
        if (value[name] == false) setAllCheck(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods.watch]);

  useEffect(() => {
    window.addEventListener('scroll', scrollChange);
    window.addEventListener('scroll', timeoutScroll);

    return () => {
      window.removeEventListener('scroll', scrollChange);
      window.removeEventListener('scroll', timeoutScroll);
    };
  }, []);

  return (
    <Page.Wrapper
      type={preview ? 'preview' : undefined}
      header={
        <>
          {!preview ? (
            <div className={cn('sticky top-0 z-10 duration-300', { 'opacity-60': scrolling })}>
              <section className="header-title">
                <div className="header-title_inner lg:w-960 relative">
                  <p
                    className={cn('title absolute transition-opacity', {
                      'opacity-0': scrollCheck,
                    })}
                  >
                    MATCHBOX入力代行【職務経歴書の編集】
                  </p>
                  <div
                    className={cn('flex items-center opacity-0 transition-opacity', {
                      'opacity-100': scrollCheck,
                    })}
                  >
                    <div className="mr-8">
                      <label className="mbx-formToggle mbx-formToggle-select">
                        <input
                          type="checkbox"
                          checked={allCheck}
                          onChange={() => changeAllCheck()}
                        />
                        <div className="mbx-formToggle-body">
                          <p className="mbx-formToggle-true">YES</p>
                          <p className="mbx-formToggle-false">NO</p>
                        </div>
                      </label>
                    </div>
                    <p className="text-14_21 whitespace-nowrap font-bold">
                      入力代行された内容をすべてに反映する
                    </p>
                  </div>
                  <div className="children-area">
                    <div className="flex justify-end sp_none">
                      <BaseButton
                        theme="secondary"
                        size="40"
                        width="large"
                        className="pr-10"
                        onClick={() => {
                          setModalContent(6);
                          setModal(true);
                        }}
                      >
                        リセットする
                      </BaseButton>
                      <BaseButton theme="primary" size="40" onClick={changePreview} width="large">
                        反映を確認する
                      </BaseButton>
                    </div>
                  </div>
                </div>
              </section>
              <div className="sp_only">
                <div className="bg-gray-200 border-b border-gray-300 h-48 flex items-center	justify-end">
                  <BaseButton
                    theme="secondary"
                    size="40"
                    width="large"
                    className="pr-10"
                    onClick={() => {
                      setModalContent(6);
                      setModal(true);
                    }}
                  >
                    リセットする
                  </BaseButton>
                  <BaseButton
                    theme="primary"
                    size="40"
                    width="large"
                    className="pr-16"
                    onClick={changePreview}
                  >
                    反映を確認する
                  </BaseButton>
                </div>
              </div>
            </div>
          ) : (
            <div className="sticky top-0 z-10">
              <HeaderTitle title="MATCHBOX入力代行【職務経歴書】">
                <div className="flex justify-end space-x-8 sp_none">
                  <BaseButton
                    className="mbx-top-preview-edit-button"
                    theme="secondary"
                    size="40"
                    width="large"
                    onClick={() => {
                      setPreview(false);
                      window.scrollTo(0, 0);
                    }}
                  >
                    編集する
                  </BaseButton>
                  <BaseButton
                    className="mbx-top-preview-edit-button"
                    theme="primary"
                    size="40"
                    width="large"
                    onClick={() => {
                      setModalContent(2);
                      setModal(true);
                    }}
                  >
                    反映を確定する
                  </BaseButton>
                </div>
              </HeaderTitle>
              <div className="sp_only">
                <div className="bg-gray-200 border-b border-gray-300 h-48 flex items-center	justify-end">
                  <BaseButton
                    className="mr-10"
                    theme="secondary"
                    size="40"
                    width="large"
                    onClick={() => {
                      setPreview(false);
                      window.scrollTo(0, 0);
                    }}
                  >
                    編集する
                  </BaseButton>
                  <BaseButton
                    className="mr-16"
                    theme="primary"
                    size="40"
                    width="large"
                    onClick={() => {
                      setModalContent(2);
                      setModal(true);
                    }}
                  >
                    反映を確定する
                  </BaseButton>
                </div>
              </div>
            </div>
          )}
        </>
      }
    >
      <div className="mbx-page-career-history mb-40 sm:mb-188 mt-20">
        <div className="sticky top-60 sm:top-100 z-10 mt-16 pb-16">
          <NotificationMatchbou
            show={notification}
            showMatchbou={false}
            onClick={() => setNotification(false)}
          >
            MATCHBOX事務局に入力代行された職務経歴書です。修正が必要な場合は編集の上、反映を確定させてください。
          </NotificationMatchbou>
        </div>
        <Page.Column
          main={
            <div className={cn({ hidden: preview })}>
              <FormContainer
                {...{
                  useFormProps,
                  onSubmit,
                  fetchData: CHData,
                  id: 'pagesCHEdit',
                  setUpdatedKey,
                  setBeforeUnload,
                  watchInitData: false,
                  methods: methods as UseFormReturn<FieldValues, Record<string, unknown>>,
                }}
              >
                <div id="summary" className="pt-56">
                  <TitleSectionTitle type="large" className="mt-12">
                    <div className="flex items-center">
                      <span className="mr-4">職務経歴概要</span>
                    </div>
                  </TitleSectionTitle>

                  <div className="space-y-8 my-22">
                    <div className="flex items-center">
                      <FormLabel label="氏名" type="small" />
                    </div>
                    <p className="text-14_20 mkt_mask_items">{user?.name}</p>
                  </div>

                  <div className="space-y-8 my-22">
                    <ToggleLabelButton name="job_check" className="mb-16 mt-32" />
                    <FormLabel label="肩書き" type="small" />
                    {values.job_check ? (
                      <FormContainerTextfield
                        className="mkt_mask_items"
                        name="job"
                        placeholder="例）UI/UXデザイナー"
                        onFocus={() => setBeforeUnload(true)}
                        emptyClear
                      />
                    ) : (
                      <div className="mkt_mask_items pointer-events-none opacity-40">
                        <div className="mbx-formContainer--textfield-wrapper">
                          <input
                            className="mbx-formContainer mbx-formContainer--textfield"
                            value={originalData?.job}
                            placeholder="例）UI/UXデザイナー"
                            readOnly
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-8 my-22">
                    <ToggleLabelButton name="description_check" className="mb-16 mt-32" />
                    <FormLabel label="職務経歴概要" type="small"></FormLabel>
                  </div>
                  {values.description_check ? (
                    <FormContainerTextArea
                      className="mkt_mask_items"
                      rows={6}
                      maxLength={300}
                      name="description"
                      placeholder="例）専門学校を卒業し、株式会社マッチワークスに入社。Webデザイナーとして受託案件のWeb制作に4年間従事。ページデザインやHTML、CSS、JavaScript、PHPでのサイト構築を一通り経験した後、UI/UX領域にスキルの幅を広げるため、株式会社マッチラボに転職。現在は、Webデザイナー経験を活かしながら、アプリ開発のUI/UXデザイナーに従事しています。"
                      onFocus={() => setBeforeUnload(true)}
                    />
                  ) : (
                    <div className="mbx-formContainer mbx-formContainer--textarea pointer-events-none opacity-40">
                      {originalData?.description ? (
                        originalData?.description
                      ) : (
                        <span className="opacity-50">
                          例）専門学校を卒業し、株式会社マッチワークスに入社。Webデザイナーとして受託案件のWeb制作に4年間従事。ページデザインやHTML、CSS、JavaScript、PHPでのサイト構築を一通り経験した後、UI/UX領域にスキルの幅を広げるため、株式会社マッチラボに転職。現在は、Webデザイナー経験を活かしながら、アプリ開発のUI/UXデザイナーに従事しています。
                        </span>
                      )}
                    </div>
                  )}

                  <div className="space-y-8 my-22">
                    <ToggleLabelButton name="advantage_check" className="mb-16 mt-32" />
                    <FormLabel label="活かせる経験・スキル・得意分野" type="small" />
                    {values.advantage_check ? (
                      <FormContainerTextArea
                        className="mkt_mask_items"
                        rows={6}
                        maxLength={300}
                        name="advantage"
                        placeholder={placeholder?.advantage}
                        onFocus={() => setBeforeUnload(true)}
                      />
                    ) : (
                      <div className="mbx-formContainer mbx-formContainer--textarea pointer-events-none opacity-40">
                        {originalData?.advantage ? (
                          originalData?.advantage
                        ) : (
                          <span className="opacity-50 whitespace-pre-wrap">
                            {placeholder?.advantage}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div id="SECTION_CAREERS">
                  <CH.Careers
                    patchCareer={setToggleCareers}
                    setDeleteCareers={setDeleteCareers}
                    setDeleteProjects={setDeleteProjects}
                    original={originalData?.careers}
                  />
                </div>

                <div id="qualification">
                  <CH.Qualifications
                    patchItem={setToggleQualifications}
                    setDeleteQualifications={setDeleteQualifications}
                    original={originalData?.qualifications}
                  />
                </div>

                <div id="skill">
                  <CH.Skills setDeleteskills={setDeleteskills} original={originalData?.skills} />
                </div>

                <div id="pr">
                  <TitleSectionTitle type="large" className="mt-12">
                    自己PR
                  </TitleSectionTitle>

                  <ToggleLabelButton name="pr_check" className="mb-16 mt-24" />
                  <div className="space-y-8 my-22">
                    {values.pr_check ? (
                      <FormContainerTextArea
                        className="mkt_mask_items"
                        rows={6}
                        maxLength={1000}
                        name="pr"
                        placeholder="例）プロデューサーやディレクターからの指示内容を単に実行するだけでなく、市場や競合の外部環境分析を自ら行い、携わるプロダクトやサービスに、今必要なUI/UXとは何かを提案し実行してきた経験が私の強みと認識しています。ユーザーにとって利便性の高い操作性も当然重要ですが、常に変化するサービスデザイン全般を意識することで、長期的にご利用いただけるサイトやアプリをご提案、構築できると考えています。"
                        onFocus={() => setBeforeUnload(true)}
                      />
                    ) : (
                      <div className="mbx-formContainer mbx-formContainer--textarea pointer-events-none opacity-40">
                        {originalData?.pr ? (
                          originalData?.pr
                        ) : (
                          <span className="opacity-50">
                            例）プロデューサーやディレクターからの指示内容を単に実行するだけでなく、市場や競合の外部環境分析を自ら行い、携わるプロダクトやサービスに、今必要なUI/UXとは何かを提案し実行してきた経験が私の強みと認識しています。ユーザーにとって利便性の高い操作性も当然重要ですが、常に変化するサービスデザイン全般を意識することで、長期的にご利用いただけるサイトやアプリをご提案、構築できると考えています。
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div id="media">
                  <TitleSectionTitle type="large" className="mt-12">
                    職務経歴書の更新日
                  </TitleSectionTitle>

                  <CH.LastUpdate />
                </div>
              </FormContainer>
            </div>
          }
          menu={<></>}
        />
        <div className={cn({ hidden: !preview })}>
          {preview && <CareerHistoryConfirmationPreview user={user} fetchData={previewChData} />}
        </div>
        <Modal isOpen={modal} isClose={() => clickModalClose()} className="rounded-modalContainer">
          <ConfirmationModal type={modalContent} object="職務経歴書">
            <div className="w-308 mt-24 mx-auto sm:w-full">
              {/* 入力代行完了 */}
              {modalContent === 1 && (
                <>
                  <BaseButton
                    className="w-full"
                    size="m"
                    theme="primary"
                    type="button"
                    onClick={() => clickModalClose()}
                  >
                    情報を確認する
                  </BaseButton>
                  <BaseButton href="/mypage" className="mt-16" size="m" theme="link" type="button">
                    マイページへ戻る
                  </BaseButton>
                </>
              )}
              {/* 反映する */}
              {modalContent === 2 && (
                <>
                  <BaseButton
                    className="w-full"
                    size="m"
                    theme="primary"
                    onClick={() => clickSubmit()}
                  >
                    反映を確定する
                  </BaseButton>
                  <BaseButton
                    className="mt-16 w-full"
                    size="m"
                    theme="secondary"
                    type="button"
                    onClick={() => setModal(false)}
                  >
                    編集へ戻る
                  </BaseButton>
                </>
              )}
              {/* 履歴書完了 */}
              {modalContent === 3 && (
                <>
                  <BaseButton
                    href="/mypage/resume/confirmation"
                    className="w-full"
                    size="m"
                    theme="primary"
                    type="button"
                  >
                    情報を確認する
                  </BaseButton>
                  <BaseButton href="/mypage" className="mt-16" size="m" theme="link" type="button">
                    マイページへ戻る
                  </BaseButton>
                </>
              )}
              {/* 完了＆オファー登録 */}
              {modalContent === 4 && (
                <>
                  <BaseButton
                    href="/mypage/direct"
                    className="w-full"
                    size="m"
                    theme="primary"
                    type="button"
                  >
                    オファー機能に登録する
                  </BaseButton>
                  <BaseButton href="/mypage" className="mt-16" size="m" theme="link" type="button">
                    マイページへ戻る
                  </BaseButton>
                </>
              )}
              {/* 完了 */}
              {modalContent === 5 && (
                <>
                  <BaseButton
                    href="/mypage"
                    className="w-full"
                    size="m"
                    theme="primary"
                    type="button"
                  >
                    マイページへ戻る
                  </BaseButton>
                </>
              )}
              {/* リセット */}
              {modalContent === 6 && (
                <>
                  <BaseButton
                    className="w-full"
                    size="m"
                    theme="primary"
                    type="button"
                    onClick={resetData}
                  >
                    リセットする
                  </BaseButton>
                  <BaseButton
                    className="mt-16 w-full"
                    size="m"
                    theme="secondary"
                    type="button"
                    onClick={() => setModal(false)}
                  >
                    編集へ戻る
                  </BaseButton>
                </>
              )}
            </div>
          </ConfirmationModal>
        </Modal>
      </div>
    </Page.Wrapper>
  );
};
